import _assign from "object-assign";

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { forwardRef, createContext, memo, useRef, useContext, useLayoutEffect, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { refType } from '@material-ui/utils';
import { useForkRef } from '@material-ui/core/utils';
import { useDensity, withStyles } from '@jpmuitk/theme';
import { useId } from '@jpmuitk/utils';
import { FixedSizeList, VariableSizeList, areEqual } from 'react-window';
import ListItemBase from './ListItemBase';
import ListItemContext from './ListItemContext';
import { useListStateContext } from './ListStateContext';
import { useListItem, useVirtualizedListItem } from './useListItem';
import { useListItemHeight } from './internal/useListItemHeight';
import { useListAutoSizer } from './internal/useListAutoSizer';
import { scrollIntoView } from './internal/scrollIntoView';
import { getWithDefault } from './internal/getWithDefault';
import { calcPreferredListHeight } from './internal/calcPreferredListHeight';
import { itemToString as defaultItemToString } from './itemToString';
export var styles = function styles(theme) {
  var _theme$toolkit = theme.toolkit,
      getContainer = _theme$toolkit.container.getContainer,
      getDisabled = _theme$toolkit.disabled.getDisabled;
  return {
    root: {
      boxSizing: 'border-box',
      overflowY: 'auto',
      '&:not($borderless)': getContainer(),
      '&:focus': {
        outline: 'none'
      }
    },
    wrapper: {
      position: 'relative'
    },
    borderless: {
      border: 'none'
    },
    disabled: getDisabled(),
    touchDensity: {},
    lowDensity: {},
    mediumDensity: {},
    highDensity: {}
  };
};
var ListboxContext = /*#__PURE__*/createContext();
var DefaultItem = /*#__PURE__*/memo(function DefaultItem(props) {
  var _useListItem = useListItem(props),
      item = _useListItem.item,
      itemToString = _useListItem.itemToString,
      itemProps = _useListItem.itemProps;

  return /*#__PURE__*/React.createElement(ListItemBase, itemProps, itemToString(item));
}, areEqual);
var DefaultVirtualizedItem = /*#__PURE__*/memo(function DefaultVirtualizedItem(props) {
  var _useVirtualizedListIt = useVirtualizedListItem(props),
      item = _useVirtualizedListIt.item,
      itemToString = _useVirtualizedListIt.itemToString,
      itemProps = _useVirtualizedListIt.itemProps;

  return /*#__PURE__*/React.createElement(ListItemBase, itemProps, itemToString(item));
}, areEqual);
/**
 * Listbox is the container for all list items. It is used as `outerElement` for
 * `react-window`.
 *
 * forwardRef gives `react-window` a way to attach a ref to listen to "scroll" events.
 * And `onScroll` is added by `react-window` so we pass it on.
 */

var Listbox = /*#__PURE__*/forwardRef(function Listbox(props, ref) {
  var _classnames;

  var style = props.style,
      onScroll = props.onScroll,
      children = props.children;

  var _useContext = useContext(ListboxContext),
      classes = _useContext.classes,
      className = _useContext.className,
      borderless = _useContext.borderless,
      density = _useContext.density,
      disabled = _useContext.disabled,
      disableFocus = _useContext.disableFocus,
      listRef = _useContext.listRef,
      styleProp = _useContext.style,
      onScrollProp = _useContext.onScroll,
      restListProps = _objectWithoutProperties(_useContext, ["classes", "className", "borderless", "density", "disabled", "disableFocus", "listRef", "style", "onScroll"]);

  var setListRef = useForkRef(ref, listRef);

  var handleScroll = function handleScroll(event) {
    if (onScroll) {
      onScroll(event);
    }

    if (onScrollProp) {
      onScrollProp(event);
    }
  };

  return /*#__PURE__*/React.createElement("div", _assign({
    className: classnames(classes.root, classes["".concat(density, "Density")], (_classnames = {}, _defineProperty(_classnames, classes.borderless, borderless), _defineProperty(_classnames, classes.disabled, disabled), _classnames), className),
    onScroll: handleScroll,
    ref: setListRef,
    style: _assign({}, style, styleProp),
    tabIndex: disabled || disableFocus ? null : 0
  }, restListProps), children);
});
process.env.NODE_ENV !== "production" ? Listbox.propTypes = {
  children: PropTypes.node.isRequired,
  onScroll: PropTypes.func,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
} : void 0;
var ListBase = /*#__PURE__*/forwardRef(function ListBase(props, ref) {
  var density = useDensity(props.density);

  var _useListStateContext = useListStateContext(),
      state = _useListStateContext.state; // Getting list id in the following order:
  // 1. Use the id prop if it's defined, otherwise..
  // 2. Use the id from list context if it's defined, or finally...
  // 3. Generate a random id.


  var generatedId = useId(props.id);
  var defaultId = getWithDefault(state.id, generatedId);
  var defaultItemHeight = useListItemHeight(density);
  var hasIndexer = typeof props.getItemAtIndex === 'function';
  var hasVariableHeight = typeof props.getItemHeight === 'function';
  var highlightedIndex = state.highlightedIndex,
      isMultiSelect = state.isMultiSelect;

  var _props$id = props.id,
      id = _props$id === void 0 ? defaultId : _props$id,
      _props$source = props.source,
      source = _props$source === void 0 ? [] : _props$source,
      borderless = props.borderless,
      _props$checkable = props.checkable,
      checkable = _props$checkable === void 0 ? isMultiSelect : _props$checkable,
      children = props.children,
      classes = props.classes,
      disableMouseDown = props.disableMouseDown,
      itemTextHighlightPattern = props.itemTextHighlightPattern,
      _props$itemCount = props.itemCount,
      itemCount = _props$itemCount === void 0 ? source.length : _props$itemCount,
      _props$itemToString = props.itemToString,
      itemToString = _props$itemToString === void 0 ? defaultItemToString : _props$itemToString,
      _props$itemHeight = props.itemHeight,
      itemHeight = _props$itemHeight === void 0 ? defaultItemHeight : _props$itemHeight,
      _props$getItemHeight = props.getItemHeight,
      getItemHeight = _props$getItemHeight === void 0 ? function () {
    return itemHeight;
  } : _props$getItemHeight,
      _props$getItemId = props.getItemId,
      getItemId = _props$getItemId === void 0 ? function (index) {
    return "".concat(id, "-item-").concat(index);
  } : _props$getItemId,
      _props$getItemIndex = props.getItemIndex,
      getItemIndex = _props$getItemIndex === void 0 ? function (item) {
    return source.indexOf(item);
  } : _props$getItemIndex,
      getItemAtIndex = props.getItemAtIndex,
      _props$overscanCount = props.overscanCount,
      overscanCount = _props$overscanCount === void 0 ? 10 : _props$overscanCount,
      _props$displayedItemC = props.displayedItemCount,
      displayedItemCount = _props$displayedItemC === void 0 ? 10 : _props$displayedItemC,
      virtualized = props.virtualized,
      width = props.width,
      height = props.height,
      maxWidth = props.maxWidth,
      maxHeight = props.maxHeight,
      minWidth = props.minWidth,
      minHeight = props.minHeight,
      ListPlaceholder = props.ListPlaceholder,
      _props$ListItem = props.ListItem,
      ListItem = _props$ListItem === void 0 ? virtualized ? DefaultVirtualizedItem : DefaultItem : _props$ListItem,
      listRefProp = props.listRef,
      restProps = _objectWithoutProperties(props, ["id", "source", "borderless", "checkable", "children", "classes", "disableMouseDown", "itemTextHighlightPattern", "itemCount", "itemToString", "itemHeight", "getItemHeight", "getItemId", "getItemIndex", "getItemAtIndex", "overscanCount", "displayedItemCount", "virtualized", "width", "height", "maxWidth", "maxHeight", "minWidth", "minHeight", "ListPlaceholder", "ListItem", "listRef"]);

  var preferredHeight = getWithDefault(height, calcPreferredListHeight({
    borderless: borderless,
    displayedItemCount: displayedItemCount,
    itemCount: itemCount,
    itemHeight: itemHeight,
    getItemHeight: getItemHeight
  }));

  var _useListAutoSizer = useListAutoSizer({
    responsive: width === undefined || height === undefined,
    height: preferredHeight,
    width: width
  }),
      _useListAutoSizer2 = _slicedToArray(_useListAutoSizer, 2),
      containerRef = _useListAutoSizer2[0],
      autoSize = _useListAutoSizer2[1];
  /**
   * This is used to access `react-window` API
   * @see https://react-window.now.sh/#/api/FixedSizeList (under `Methods`)
   */


  var virtualizedListRef = useRef();
  var listRef = useRef();
  var setListRef = useForkRef(listRef, listRefProp);
  var setContainerRef = useForkRef(ref, containerRef);

  var scrollToIndex = function scrollToIndex(itemIndex) {
    scrollIntoView(listRef.current.querySelector("[data-option-index=\"".concat(itemIndex, "\"]")), listRef, _toConsumableArray(listRef.current.querySelectorAll('[data-option-index]')));
  };

  var scrollHandles = useMemo(function () {
    return {
      scrollToIndex: scrollToIndex,
      scrollToItem: function scrollToItem(item) {
        scrollToIndex(getItemIndex(item));
      },
      scrollTo: function scrollTo(scrollOffset) {
        listRef.current.scrollTop = scrollOffset;
      }
    };
  }, [getItemIndex]);
  var virtualizedScrollHandles = useMemo(function () {
    return {
      scrollToIndex: function scrollToIndex(itemIndex) {
        virtualizedListRef.current.scrollToItem(itemIndex);
      },
      scrollToItem: function scrollToItem(item) {
        virtualizedListRef.current.scrollToItem(getItemIndex(item));
      },
      scrollTo: function scrollTo(scrollOffset) {
        virtualizedListRef.current.scrollTo(scrollOffset);
      }
    };
  }, [getItemIndex]);
  useImperativeHandle(ref, function () {
    if (virtualized && virtualizedListRef.current) {
      return virtualizedScrollHandles;
    } else if (listRef.current) {
      return scrollHandles;
    }
  }, [virtualized, scrollHandles, virtualizedScrollHandles]);
  useLayoutEffect(function () {
    if (highlightedIndex == null) {
      return;
    }

    if (virtualized && virtualizedListRef.current) {
      virtualizedListRef.current.scrollToItem(highlightedIndex);
    } else if (listRef.current) {
      scrollToIndex(highlightedIndex);
    }
  }, [highlightedIndex, virtualized]);

  var renderList = function renderList() {
    if (React.Children.count(children)) {
      return /*#__PURE__*/React.createElement(Listbox, {
        style: autoSize
      }, /*#__PURE__*/React.createElement(ListItemContext.Provider, {
        value: {
          density: density,
          getItemId: getItemId,
          getItemHeight: getItemHeight,
          itemToString: itemToString,
          itemTextHighlightPattern: itemTextHighlightPattern,
          disableMouseDown: disableMouseDown,
          checkable: checkable
        }
      }, children));
    }

    if (virtualized) {
      var VirtualizedList = hasVariableHeight ? VariableSizeList : FixedSizeList;
      return /*#__PURE__*/React.createElement(ListItemContext.Provider, {
        value: {
          density: density,
          getItemId: getItemId,
          itemToString: itemToString,
          itemTextHighlightPattern: itemTextHighlightPattern,
          disableMouseDown: disableMouseDown,
          checkable: checkable
        }
      }, /*#__PURE__*/React.createElement(VirtualizedList, {
        height: autoSize.height,
        itemCount: itemCount,
        itemData: source,
        itemSize: hasVariableHeight ? getItemHeight : itemHeight,
        outerElementType: Listbox,
        overscanCount: overscanCount,
        ref: virtualizedListRef,
        width: autoSize.width
      }, ListItem));
    }

    return /*#__PURE__*/React.createElement(Listbox, {
      style: autoSize
    }, /*#__PURE__*/React.createElement(ListItemContext.Provider, {
      value: {
        density: density,
        getItemId: getItemId,
        getItemHeight: getItemHeight,
        itemToString: itemToString,
        itemTextHighlightPattern: itemTextHighlightPattern,
        disableMouseDown: disableMouseDown,
        checkable: checkable
      }
    }, (hasIndexer ? Array.from({
      length: itemCount
    }) : source).map(function (item, index) {
      return /*#__PURE__*/React.createElement(ListItem, {
        index: index,
        item: hasIndexer ? getItemAtIndex(index) : item,
        key: getItemId(index)
      });
    })));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classes.wrapper,
    ref: setContainerRef,
    style: {
      minWidth: minWidth,
      minHeight: minHeight,
      width: getWithDefault(width, '100%'),
      height: getWithDefault(height, '100%'),
      maxWidth: getWithDefault(maxWidth, width),
      maxHeight: getWithDefault(maxHeight, preferredHeight)
    }
  }, itemCount === 0 && ListPlaceholder !== undefined ? /*#__PURE__*/React.createElement(ListPlaceholder, {
    style: autoSize
  }) : /*#__PURE__*/React.createElement(ListboxContext.Provider, {
    value: _assign({}, restProps, {
      listRef: setListRef,
      id: id,
      classes: classes,
      density: density,
      borderless: borderless
    })
  }, renderList()));
});
process.env.NODE_ENV !== "production" ? ListBase.propTypes = {
  /**
   * The component used for item instead of the default.
   */
  ListItem: PropTypes.func,

  /**
   * The component used when there are no items.
   */
  ListPlaceholder: PropTypes.func,

  /**
   * If `true`, the component will have no border.
   */
  borderless: PropTypes.bool,

  /**
   * Adds checkbox to list. Only valid for multiselect variant.
   */
  checkable: PropTypes.bool,

  /**
   * Item nodes if the component is used declaratively.
   */
  children: PropTypes.node,

  /**
   * The className(s) of the component.
   */
  className: PropTypes.string,

  /**
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /**
   * The density of a component affects the style of the layout.
   *
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * If `true`, the component will not receive focus.
   *
   * Useful when list is used with other components to form a tightly coupled atomic component where
   * other components should receive focus instead. For instance, when used with an input to form a
   * combo box the list should not be focusable, the input should receive focus instead.
   */
  disableFocus: PropTypes.bool,

  /**
   * If `true`, the component will prevent mousedown event from firing.
   *
   * Useful when list is used with other components to form a tightly coupled atomic component where
   * a click/select on list should not cause the focus to blur away from the current active element.
   */
  disableMouseDown: PropTypes.bool,

  /**
   * If `true`, the component will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * The number of items displayed in the visible area.
   *
   * Note that this determines the max height of the list if the list height is not set to 100%.
   *
   * @default 10
   */
  displayedItemCount: PropTypes.number,

  /**
   * The indexer function used when there is no source. It should return a number.
   *
   * @param {number} index The item index.
   */
  getItemAtIndex: PropTypes.func,

  /**
   * Used for providing customized item height. It should return a number or a string if item height
   * is in percentage. When used with `virtualized` prop a variable-height list will be rendered instead
   * of a fixed-height one.
   *
   * @param {number} index The item index.
   */
  getItemHeight: PropTypes.func,

  /**
   * Used for providing customized item ids.
   *
   * @param {number} index The item index.
   */
  getItemId: PropTypes.func,

  /**
   * The function for getting item's index.
   *
   * @param {object} item The item.
   */
  getItemIndex: PropTypes.func,

  /**
   * Height of the component.
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Id of the component. If omitted a randomly generated id will be used.
   */
  id: PropTypes.string,

  /**
   * The total number of items.
   *
   * Used for keyboard navigation (when `End` key is pressed) and when the list is virtualized.
   *
   * @default source.length
   */
  itemCount: PropTypes.number,

  /**
   * Height of an item. I can be a number or a string if item height is in percentage. If omitted
   * default height values from Toolkit theme will be used.
   *
   * Note that when using a percentage value, the list must have a height.
   */
  itemHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Used for providing text highlight.
   *
   * It can be a capturing regex or a string for a straightforward string matching.
   */
  itemTextHighlightPattern: PropTypes.oneOfType([PropTypes.instanceOf(RegExp), PropTypes.string]),

  /**
   * Item `toString` function when list is not used declaratively and its items are objects
   * instead of strings. The string value is also used in tooltip when item text is truncated.
   *
   * If omitted, component will look for a `label` property on the data object.
   *
   * @param {object} item The item.
   */
  itemToString: PropTypes.func,

  /**
   * Used for accessing the scrollable list node inside of the component. If you want to access
   * the outer wrapper node use `ref` instead.
   */
  listRef: refType,

  /**
   * Maximum list height.
   */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Maximum list width.
   */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Minimum list height.
   */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Minimum list width.
   */
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * @external - react-window
   *
   * The number of items to render outside of the visible area.
   *
   * @default 10
   */
  overscanCount: PropTypes.number,

  /**
   * If `true`, the component will remember the last keyboard-interacted position
   * and highlight it when list is focused again.
   */
  restoreLastFocus: PropTypes.bool,

  /**
   * Data source used. It should be an array of objects or strings.
   */
  source: PropTypes.arrayOf(PropTypes.any),

  /**
   * @external - react-window
   *
   * If `true`, list will be virtualized.
   * @see https://github.com/bvaughn/react-window
   */
  virtualized: PropTypes.bool,

  /**
   * Width of the component.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
} : void 0;
export default withStyles(styles, {
  name: 'List'
})(ListBase);